import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShoppingBag, CreditCard, ShoppingBasket, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { useBrand } from '../context/BrandContext';

export const CartDrawer: React.FC = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, cartTotalOriginal, cartTotalDiscount, isCartOpen, setIsCartOpen } = useCart();
  const { user, signIn } = useUser();
  const { resolveMediaUrl } = useBrand();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-ows-dark/40 backdrop-blur-sm z-[60]"
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-ows-white shadow-2xl z-[70] p-5 sm:p-8 flex flex-col border-l border-ows-teal/10"
            >
              {/* Tactical Overlays */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03] overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(rgba(18,52,59,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(18,52,59,0.1)_1px,transparent_1px)] bg-[size:20px_20px]" />
                <div className="absolute top-10 left-10 font-mono text-[8px] uppercase tracking-widest">Sector: 07-A / Loadout</div>
                <div className="absolute bottom-10 right-10 font-mono text-[8px] uppercase tracking-widest">OWS-OWS EXPEDITION CX2 / System-Ready</div>
              </div>

              <div className="flex justify-between items-center mb-8 sm:mb-12 relative z-10">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-ows-teal/10 rounded-full flex items-center justify-center">
                    <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 text-ows-teal" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-display font-light uppercase tracking-widest text-ows-dark">Tactical Loadout</h2>
                    <p className="text-[8px] font-bold uppercase tracking-[0.3em] text-ows-teal font-mono">Mission Inventory</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsCartOpen(false)}
                  className="p-2.5 sm:p-3 hover:bg-ows-sand rounded-full transition-all group"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6 text-ows-dark/40 group-hover:text-ows-dark group-hover:rotate-90 transition-all" />
                </button>
              </div>



              <div className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar relative z-10">
                {cart.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-ows-sand/50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ShoppingBasket className="h-10 w-10 text-ows-dark/10" />
                    </div>
                    <p className="text-ows-dark/40 text-[10px] font-bold uppercase tracking-[0.3em] mb-8">Your bag is empty</p>
                    <button 
                      onClick={() => setIsCartOpen(false)}
                      className="text-ows-teal text-[10px] font-bold uppercase tracking-[0.3em] border-b-2 border-ows-teal pb-1 hover:text-ows-dark hover:border-ows-dark transition-all"
                    >
                      Continue Exploring
                    </button>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div key={item.id} className="flex gap-6 group">
                      <div className="w-24 h-24 bg-ows-sand/20 overflow-hidden rounded-2xl flex-shrink-0">
                        <img 
                          src={resolveMediaUrl(item.image) || undefined} 
                          alt={item.name} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                          referrerPolicy="no-referrer" 
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div>
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="text-xs font-bold uppercase tracking-widest leading-tight">{item.name}</h4>
                            <button 
                              onClick={() => removeFromCart(item.id)} 
                              className="text-ows-dark/20 hover:text-red-500 transition-colors p-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-[10px] text-ows-dark/40 uppercase tracking-widest mb-2">{item.category}</p>
                          <p className="text-ows-teal text-sm font-bold">${item.price}</p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex items-center bg-ows-sand/30 rounded-lg p-1">
                            <button 
                              onClick={() => updateQuantity(item.id, -1)} 
                              className="w-8 h-8 flex items-center justify-center text-ows-dark/40 hover:text-ows-teal transition-colors"
                            >
                              -
                            </button>
                            <span className="w-8 text-center text-[10px] font-bold">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, 1)} 
                              className="w-8 h-8 flex items-center justify-center text-ows-dark/40 hover:text-ows-teal transition-colors"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {cart.length > 0 && (
                <div className="mt-12 pt-12 border-t border-ows-teal/10">
                  <div className="space-y-4 mb-10">
                    <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.2em] text-ows-dark/40">
                      <span>Subtotal</span>
                      <span className="text-ows-dark">${cartTotalOriginal}</span>
                    </div>
                    {cartTotalDiscount > 0 && (
                      <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.2em] text-ows-teal">
                        <span>Member Discount</span>
                        <span>-${cartTotalDiscount}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.2em] text-ows-dark/40">
                      <span>Shipping</span>
                      <span className="text-ows-teal">$100 Flat / Free Pickup</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.2em] text-ows-dark/40">
                      <span>Sales Tax</span>
                      <span className="text-ows-teal font-mono">Calculated at checkout</span>
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-ows-teal/5">
                      <span className="text-xs font-bold uppercase tracking-[0.3em]">Total</span>
                      <span className="text-2xl sm:text-3xl font-serif font-bold text-ows-teal">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={handleCheckout}
                    className="w-full py-5 sm:py-6 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.4em] hover:bg-ows-dark transition-all flex items-center justify-center gap-3 sm:gap-4 rounded-2xl shadow-xl shadow-ows-teal/20"
                  >
                    <CreditCard className="h-4 w-4 shrink-0" />
                    {cart.some(item => item.name.toLowerCase().includes('c2')) ? "Secure Pre-Order Spot" : "Initiate Checkout"}
                  </button>
                  
                  <p className="text-center mt-6 text-[8px] text-ows-dark/30 uppercase tracking-[0.2em]">
                    Secure encrypted transaction via Stripe
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Floating Cart Button */}
      {cart.length > 0 && !isCartOpen && (
        <motion.button 
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-8 right-8 bg-ows-teal text-white p-6 shadow-2xl z-50 group rounded-full"
        >
          <ShoppingCart className="h-6 w-6" />
          <span className="absolute -top-2 -right-2 bg-ows-orange text-white w-6 h-6 rounded-full text-[10px] flex items-center justify-center font-bold">
            {cart.reduce((sum, item) => sum + item.quantity, 0)}
          </span>
        </motion.button>
      )}
    </>
  );
};
